import { useCallback, useState } from 'react'
import { useBookAppointment } from '@/api/enrollment-api'
import { apiClient } from '@/api/client'
import { useNavigate } from '@/router/navigation'
import { getStoredAppointmentFormData } from '../storage'
import type { RegisteredOnlineSession, TimeSlotSelection } from '../types'

type UseScheduleAppointmentBookingParams = {
  canContinue: boolean
  selectedIsoDate: string
  selectedLocationExternalId: string | null
  selection: TimeSlotSelection | null
}

type RequestError = {
  response?: {
    data?: {
      message?: string
    }
  }
}

function getRequestErrorMessage(error: unknown, fallback: string) {
  const axiosMessage = (error as RequestError)?.response?.data?.message

  return axiosMessage ?? (error instanceof Error ? error.message : fallback)
}

export function useScheduleAppointmentBooking({
  canContinue,
  selectedIsoDate,
  selectedLocationExternalId,
  selection
}: UseScheduleAppointmentBookingParams) {
  const navigate = useNavigate()
  const bookAppointment = useBookAppointment()
  const [registeredOnlineSession, setRegisteredOnlineSession] =
    useState<RegisteredOnlineSession | null>(null)
  const [bookingError, setBookingError] = useState<string | null>(null)

  const handleOnlineRegistrationContinue = useCallback(() => {
    setRegisteredOnlineSession(null)
    navigate('/endorsements')
  }, [navigate])

  const handleContinue = useCallback(async () => {
    if (!canContinue) return

    setBookingError(null)

    const { contactExternalId, email, firstName, lastName, phoneNumber } =
      getStoredAppointmentFormData()

    if (selection?.type === 'online') {
      if (selection.eventId == null) {
        setBookingError('Unable to register for this online session right now. Please try again.')
        return
      }

      try {
        await apiClient.post(`/api/v1/webinars/${selection.eventId}/register`, {
          email,
          firstName,
          lastName,
          phone: phoneNumber
        })

        setRegisteredOnlineSession({
          date: selectedIsoDate,
          time: selection.time
        })
      } catch (error) {
        setBookingError(
          getRequestErrorMessage(
            error,
            'Unable to register for your online session right now. Please try again.'
          )
        )
      }

      return
    }

    try {
      await bookAppointment.mutateAsync({
        trainingLocationExternalId: selectedLocationExternalId ?? '',
        appointmentType: selection?.type === 'inPerson' ? 'IN_PERSON' : 'PHONE',
        appointmentDate: selectedIsoDate,
        appointmentTime: selection?.rawTime ?? '',
        contactExternalId,
        firstName,
        lastName,
        email,
        phoneNumber
      })

      navigate('/endorsements')
    } catch (error) {
      setBookingError(
        getRequestErrorMessage(
          error,
          'Unable to book your appointment right now. Please try again.'
        )
      )
    }
  }, [
    bookAppointment,
    canContinue,
    navigate,
    selectedIsoDate,
    selectedLocationExternalId,
    selection
  ])

  return {
    bookAppointment,
    bookingError,
    handleContinue,
    handleOnlineRegistrationContinue,
    navigate,
    registeredOnlineSession,
    setBookingError
  }
}
